import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Badge,
  Box,
  Fab,
  Paper,
  IconButton,
  Typography,
  Tooltip,
  Fade,
  LinearProgress,
  useTheme,
  Stack,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull as ExpandIcon,
  VerticalSplit as SideCarIcon,
  CloseFullscreen as ExitSideCarIcon,
  NorthWest as ResizeIcon,
  Add as NewChatIcon,
  History as HistoryIcon,
  KeyboardArrowDown as ModelMenuIcon,
} from "@mui/icons-material";
import { ChatInterface } from "./ChatInterface";
import HelpHint from "../HelpHint";
import { useChatContext } from "../../hooks/useChatContext";
import { useAgentChatClient } from "../../hooks/useAgentChatClient";
import { useNavigationHandler } from "../../hooks/useNavigationHandler";
import { AgentModelSelection, AgentProviderOption, Message } from "../../types/chat";

const MIN_HEIGHT = 400;
const MIN_WIDTH = 300; // Allow it to go slightly smaller
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 600;

const formatModelName = (model: string): string => {
  const normalized = model.toLowerCase();
  if (normalized === "gpt-4o-mini") return "GPT-4o mini";
  if (normalized === "gpt-5-mini") return "GPT-5 mini";
  if (normalized.startsWith("claude-sonnet")) return "Claude Sonnet";
  if (normalized.startsWith("claude-opus")) return "Claude Opus";
  if (normalized.startsWith("claude-haiku")) return "Claude Haiku";
  return model;
};

interface FloatingChatProps {
  position?: "bottom-right" | "bottom-left";
}

export const FloatingChat: React.FC<FloatingChatProps> = ({
  position = "bottom-right",
}) => {
  const theme = useTheme();

  // States
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"floating" | "sidecar">("floating");
  const [isMinimized, setIsMinimized] = useState(false);
  // const [unreadCount, setUnreadCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [modelOptions, setModelOptions] = useState<AgentProviderOption[]>([]);
  const [selectedModel, setSelectedModel] = useState<AgentModelSelection | null>(null);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [modelLoadFailed, setModelLoadFailed] = useState(false);
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(null);

  // Resize State
  const [size, setSize] = useState({
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  });
  const [isResizing, setIsResizing] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const newChatRef = useRef<() => void>(() => {});
  const historyRef = useRef<() => void>(() => {});

  const chatContext = useChatContext();
  const agentClient = useAgentChatClient();
  const { processToolOperations, isNavigating } = useNavigationHandler({
    onModalOpen: (modalName, params) => {
      window.dispatchEvent(
        new CustomEvent("ai-open-modal", { detail: { modalName, params } }),
      );
    },
  });

  const isRight = position === "bottom-right";

  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setIsModelLoading(true);
    setModelLoadFailed(false);
    setModelMenuAnchor(null);
    setSelectedModel(null);

    const loadModels = async () => {
      if (!active) return;
      setIsModelLoading(true);
      try {
        const result = await agentClient.getModelOptions(chatContext.sessionId);
        if (!active) return;
        // Never advertise providers that the server cannot actually construct.
        // This also keeps placeholder defaults (for example Gemini without a key)
        // out of the conversation-level model picker.
        const visibleProviders = result.providers.filter((provider) => provider.configured && provider.models.length > 0);
        setModelOptions(visibleProviders);
        const requestedModel = result.selectedModel || result.defaultModel;
        const requestedIsVisible = visibleProviders.some((provider) =>
          provider.provider === requestedModel?.provider && provider.models.includes(requestedModel.model));
        const firstVisible = visibleProviders[0]?.models[0]
          ? { provider: visibleProviders[0].provider, model: visibleProviders[0].models[0] }
          : null;
        setSelectedModel(requestedIsVisible ? requestedModel : firstVisible);
        setModelLoadFailed(false);
      } catch (error) {
        if (!active) return;
        console.warn("Failed to load chat model options", error);
        setModelOptions([]);
        setSelectedModel(null);
        setModelLoadFailed(true);
        retryTimer = setTimeout(() => void loadModels(), 5000);
      } finally {
        if (active) setIsModelLoading(false);
      }
    };

    void loadModels();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [agentClient, chatContext.sessionId, isOpen]);

  const requestNotificationPermission = useCallback(() => {
    if (!("Notification" in window) || Notification.permission !== "default") return;
    void Notification.requestPermission().catch(() => undefined);
  }, []);

  const openChatSession = useCallback((sessionId?: string) => {
    window.focus();
    if (sessionId && sessionId !== chatContext.sessionId) {
      agentClient.resumeSession(sessionId);
      chatContext.loadSessionId(sessionId);
    }
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0);
  }, [agentClient, chatContext]);

  const handleAssistantMessage = useCallback((message: Message, sessionId: string) => {
    const chatIsVisible = isOpen && !isMinimized && !document.hidden && document.hasFocus();
    const needsApproval = Boolean(message.pendingAction);
    const notificationBody = needsApproval
      ? message.pendingAction?.summary || "Prospo AI is waiting for your approval."
      : message.content.replace(/[*_`#>]/g, "").replace(/\s+/g, " ").trim().slice(0, 180) || "Your task has finished.";
    // A just-created meeting gets a meeting-specific, clickable bell notification
    // (navigates to /meetings/:id — see Notifications.tsx's meetingId branch),
    // instead of the generic one below, which only reopens this chat session.
    window.dispatchEvent(new CustomEvent('prospo:add-agent-notification', { detail: message.meetingLink ? {
      id: `agent-${sessionId}-${Date.now()}`,
      meetingId: message.meetingLink.meetingId,
      status: 'success',
      title: 'Meeting scheduled',
      message: `${message.meetingLink.title} has been scheduled.`,
      createdAt: new Date().toISOString(),
      isRead: false,
      isSeen: false,
    } : {
      id: `agent-${sessionId}-${Date.now()}`,
      type: 'agent',
      sessionId,
      status: 'success',
      title: needsApproval ? 'Prospo AI needs your approval' : 'Prospo AI task completed',
      message: notificationBody,
      createdAt: new Date().toISOString(),
      isRead: false,
      isSeen: false,
    } }));
    if (chatIsVisible) return;

    setUnreadCount((count) => count + 1);
    if (!("Notification" in window) || Notification.permission !== "granted") return;

    try {
      const notification = new Notification(
        needsApproval ? "Prospo AI needs your approval" : "Prospo AI task completed",
        { body: notificationBody, icon: "/logo.png", tag: `prospo-ai-${sessionId}`, requireInteraction: needsApproval },
      );
      notification.onclick = () => {
        notification.close();
        openChatSession(sessionId);
      };
    } catch {
      // The unread badge remains available when the browser cannot display a native notification.
    }
  }, [isMinimized, isOpen, openChatSession]);

  useEffect(() => {
    const handleOpenAgentSession = (event: Event) => {
      openChatSession((event as CustomEvent<{ sessionId?: string }>).detail?.sessionId);
    };
    window.addEventListener('prospo:open-agent-session', handleOpenAgentSession);
    return () => window.removeEventListener('prospo:open-agent-session', handleOpenAgentSession);
  }, [openChatSession]);

  useEffect(() => {
    const clearWhenVisible = () => {
      if (isOpen && !isMinimized && !document.hidden) setUnreadCount(0);
    };
    clearWhenVisible();
    document.addEventListener("visibilitychange", clearWhenVisible);
    window.addEventListener("focus", clearWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", clearWhenVisible);
      window.removeEventListener("focus", clearWhenVisible);
    };
  }, [isMinimized, isOpen]);

  // --- Global Layout & Collision Broadcasting ---
  useEffect(() => {
    // 1. Manage CSS Classes for Layout
    if (isOpen && !isMinimized) {
      document.body.classList.add("prospo-chat-open");
      if (viewMode === "sidecar") {
        document.body.classList.add("prospo-sidecar-active");
        document.body.style.setProperty(
          "--prospo-sidecar-width",
          `${size.width}px`,
        );
      } else {
        document.body.classList.remove("prospo-sidecar-active");
        document.body.style.removeProperty("--prospo-sidecar-width");
      }
    } else { 
      document.body.classList.remove(
        "prospo-chat-open",
        "prospo-sidecar-active",
      );
      document.body.style.removeProperty("--prospo-sidecar-width");
    }

    // 2. Dispatch Resize Event (Debounced via RequestAnimationFrame)
    window.dispatchEvent(new Event("resize"));

    // 3. Robust Collision Broadcasting using ResizeObserver
    // This fixes the lag by only updating when dimensions actually change
    if (!chatRef.current) return;

    const broadcastState = () => {
      if (!chatRef.current) return;
      const rect = chatRef.current.getBoundingClientRect();

      window.dispatchEvent(
        new CustomEvent("prospo-chat-update", {
          detail: {
            isOpen,
            isMinimized,
            viewMode,
            rect: {
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
              width: rect.width,
              height: rect.height,
            },
          },
        }),
      );
    };

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(broadcastState);
    });

    resizeObserver.observe(chatRef.current);

    // Broadcast immediately on state change
    broadcastState();

    return () => {
      resizeObserver.disconnect();
      document.body.classList.remove(
        "prospo-chat-open",
        "prospo-sidecar-active",
      );
      document.body.style.removeProperty("--prospo-sidecar-width");
    };
  }, [isOpen, viewMode, isMinimized, size.width]); // Removed size.height to prevent loop

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        requestNotificationPermission();
        setIsOpen((prev) => !prev);
        if (!isOpen) setIsMinimized(false);
      }
      if (event.key === "Escape" && isOpen) {
        if (viewMode === "sidecar") setViewMode("floating");
        else setIsMinimized(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, requestNotificationPermission, viewMode]);

  // --- Unified Resizing Logic ---
  const pendingResizeFrame = useRef<number | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Native mousemove can fire far more often than the screen repaints
      // (well over 60/s on a high-poll-rate mouse/trackpad) — calling
      // setSize() on every single one used to cascade into a React
      // re-render, a body style write, and a `window.dispatchEvent(resize)`
      // (which every layout-measuring listener across the whole app reacts
      // to) that many times per second, which is real, visible jank
      // regardless of the CSS-transition fix above. Coalescing to one
      // requestAnimationFrame keeps all of that to at most once per
      // rendered frame, using only the latest mouse position.
      if (pendingResizeFrame.current !== null) return;
      pendingResizeFrame.current = requestAnimationFrame(() => {
        pendingResizeFrame.current = null;

        let newWidth: number;
        let newHeight: number;

        if (viewMode === "sidecar") {
          // In Sidecar: We only resize WIDTH by dragging the left edge
          // Logic: Total Screen Width - Mouse X Position
          newWidth = window.innerWidth - e.clientX;
          newHeight = window.innerHeight; // Sidecar is always full height
        } else {
          // In Floating: Resize both
          newWidth = isRight
            ? window.innerWidth - e.clientX - 24
            : e.clientX - 24;
          newHeight = window.innerHeight - e.clientY - 24;
        }

        setSize({
          width: Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH)),
          height:
            viewMode === "sidecar"
              ? newHeight
              : Math.max(MIN_HEIGHT, Math.min(newHeight, 1000)),
        });
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
      document.body.classList.remove("prospo-resizing");
      if (pendingResizeFrame.current !== null) {
        cancelAnimationFrame(pendingResizeFrame.current);
        pendingResizeFrame.current = null;
      }
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      // Force cursor style during drag
      document.body.style.cursor =
        viewMode === "sidecar" ? "col-resize" : "nwse-resize";
      document.body.style.userSelect = "none";
      // Suppresses every dependent layout's own eased transition (main
      // content margin, header width, version-history drawer, …) for the
      // duration of the drag — see the CSS rule in index.css for why.
      document.body.classList.add("prospo-resizing");
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("prospo-resizing");
      if (pendingResizeFrame.current !== null) {
        cancelAnimationFrame(pendingResizeFrame.current);
        pendingResizeFrame.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing, isRight, viewMode]); // Removing specific deps to ensure it uses fresh state

  const getPaperStyles = () => {
    if (isMinimized) {
      return {
        width: "320px",
        height: "48px",
        bottom: 24,
        right: 24,
        borderRadius: "12px",
      };
    }

    if (viewMode === "sidecar") {
      return {
        top: 0,
        right: 0,
        bottom: 0,
        width: `${size.width}px`, // [UPDATED] Use dynamic width
        height: "100vh",
        borderRadius: 0,
        maxHeight: "100vh",
        borderLeft: `1px solid ${theme.palette.divider}`,
      };
    }

    return {
      width: `${size.width}px`,
      height: `${size.height}px`,
      bottom: 24,
      right: 24,
      borderRadius: "20px",
      maxHeight: "calc(100vh - 48px)",
    };
  };

  return (
    <>
      {/* FAB Launcher */}
      <Fade in={!isOpen}>
        <Tooltip title="Prospo AI (Ctrl+K)" placement="left">
          <Fab
            color="primary"
            aria-label="Prospo AI"
            onClick={() => {
              requestNotificationPermission();
              setIsOpen(true);
              setIsMinimized(false);
              setUnreadCount(0);
            }}
            sx={{
              position: "fixed",
              bottom: 50,
              right: 0,
              zIndex: 1200,
              display: isOpen ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 130,
              borderRadius: "12px 0 0 12px",
              boxShadow: theme.shadows[8],
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Badge badgeContent={unreadCount} color="error" max={9}>
            <Stack flexDirection="row" alignItems="center">
              <Typography
                variant="body1"
                sx={{
                  rotate: "-90deg",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  color: "#FFFFFF",
                }}
              >
                Prospo Agent
              </Typography>
            </Stack>
            </Badge>
          </Fab>
        </Tooltip>
      </Fade>

      {/* Main Window */}
      <Paper
        ref={chatRef}
        elevation={viewMode === "sidecar" ? 0 : 24}
        className={viewMode === "sidecar" ? "prospo-sidecar-panel" : ""}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          zIndex: 1300,
          display: isOpen ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.paper",
          transition: isResizing
            ? "none"
            : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          ...getPaperStyles(),
        }}
      >
        {/* [NEW] Sidecar Resize Handle (Left Strip) */}
        {!isMinimized && viewMode === "sidecar" && (
          <Box
            onMouseDown={() => setIsResizing(true)}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "3px",
              cursor: "col-resize",
              zIndex: 1350,
              "&:hover": { bgcolor: "primary.main" },
              transition: "background-color 0.2s",
            }}
          />
        )}

        {/* Floating Mode Resize Handle (Corner) */}
        {!isMinimized && viewMode === "floating" && (
          <Box
            onMouseDown={() => setIsResizing(true)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 30,
              height: 30,
              cursor: "nwse-resize",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 50%)",
              borderBottomRightRadius: 20,
            }}
          >
            <ResizeIcon
              sx={{
                fontSize: 14,
                color: "text.disabled",
                transform: "rotate(90deg) translate(-2px, -2px)",
              }}
            />
          </Box>
        )}

        {/* Header */}
        <Box
          onClick={() => isMinimized && setIsMinimized(false)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: isMinimized ? "0 16px" : "16px 20px",
            height: isMinimized ? "100%" : "auto",
            background: isMinimized
              ? "linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)"
              : "rgba(255, 255, 255, 0.95)",
            borderBottom: isMinimized ? "none" : "1px solid rgba(0,0,0,0.06)",
            cursor: "pointer",
            color: isMinimized ? "white" : "text.primary",
            userSelect: "none",
            position: "relative",
            flexShrink: 0,
            flexWrap: isMinimized ? "nowrap" : "wrap",
          }}
        >
          {isProcessing && !isMinimized && (
            <LinearProgress
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: 2,
                "& .MuiLinearProgress-bar": { bgcolor: "#2196f3" },
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: !isMinimized && viewMode === "floating" ? 2 : 0,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: isProcessing
                  ? "#ffa726"
                  : isMinimized
                    ? "#4caf50"
                    : "primary.main",
                boxShadow: isProcessing
                  ? "0 0 8px rgba(255, 167, 38, 0.6)"
                  : "0 0 8px rgba(25, 118, 210, 0.4)",
                transition: "background-color 0.3s",
              }}
            />
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ letterSpacing: "-0.01em", color: "inherit", lineHeight: 1.15 }}
            >
              Prospo AI
            </Typography>
          </Box>

          <Menu
            anchorEl={modelMenuAnchor}
            open={Boolean(modelMenuAnchor)}
            onClose={() => setModelMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 210, maxWidth: 280, mt: 0.5 } } }}
          >
            {modelOptions.flatMap((provider) => provider.models.map((model) => {
              const isSelected = selectedModel?.provider === provider.provider
                && selectedModel.model === model;
              return (
                <MenuItem
                  key={`${provider.provider}:${model}`}
                  selected={isSelected}
                  onClick={() => {
                    setSelectedModel({ provider: provider.provider, model });
                    setModelMenuAnchor(null);
                  }}
                  sx={{ py: 0.75 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>
                      {formatModelName(model)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {provider.label}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            }))}
          </Menu>

          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isMinimized && (
              <>
                <HelpHint text="Type / for commands." sx={{ color: 'text.secondary' }} />
                <Tooltip title="View history">
                  <IconButton
                    size="small"
                    onClick={() => {
                      historyRef.current();
                    }}
                    sx={{ color: isMinimized ? 'rgba(255,255,255,0.9)' : 'text.secondary' }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New Chat">
                  <IconButton
                    size="small"
                    onClick={() => {
                      newChatRef.current();
                    }}
                    sx={{ color: isMinimized ? 'rgba(255,255,255,0.9)' : 'text.secondary', mr: 1 }}
                  >
                    <NewChatIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {!isMinimized && (
              <Tooltip
                title={viewMode === "sidecar" ? "Exit Side-Car" : "Dock to Side"}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setViewMode((prev) =>
                      prev === "sidecar" ? "floating" : "sidecar",
                    )
                  }
                  sx={{ color: "text.secondary" }}
                >
                  {viewMode === "sidecar" ? (
                    <ExitSideCarIcon fontSize="small" />
                  ) : (
                    <SideCarIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{
                  color: isMinimized
                    ? "rgba(255,255,255,0.8)"
                    : "text.secondary",
                }}
              >
                {isMinimized ? (
                  <ExpandIcon fontSize="small" />
                ) : (
                  <MinimizeIcon
                    fontSize="small"
                    sx={{ transform: "translateY(-1px)" }}
                  />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{
                  color: isMinimized
                    ? "rgba(255,255,255,0.8)"
                    : "text.secondary",
                  "&:hover": {
                    color: "#d32f2f",
                    bgcolor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {!isMinimized && (
            <Box
              sx={{ width: "100%", mt: 1 }}
              onClick={(event) => event.stopPropagation()}
            >
              <Tooltip
                title={modelLoadFailed
                  ? "Model options could not be loaded. Retrying automatically…"
                  : "Model for this conversation. Changes apply from your next message and keep the current chat history."}
              >
                <span style={{ display: "block", width: "100%" }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={isProcessing || isModelLoading || !selectedModel}
                    onClick={(event) => setModelMenuAnchor(event.currentTarget)}
                    endIcon={isModelLoading
                      ? <CircularProgress size={14} />
                      : <ModelMenuIcon sx={{ fontSize: "18px !important" }} />}
                    sx={{
                      height: 38,
                      px: 1.25,
                      justifyContent: "flex-start",
                      border: "1px solid #d0d5dd !important",
                      borderRadius: 1.5,
                      bgcolor: "#ffffff !important",
                      color: "#344054 !important",
                      textTransform: "none",
                      boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                      "&:hover": {
                        borderColor: "#1976d2 !important",
                        bgcolor: "#f8fbff !important",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#e4e7ec !important",
                        bgcolor: "#f9fafb !important",
                        color: "#98a2b3 !important",
                      },
                      "& .MuiButton-endIcon": { ml: "auto", mr: 0 },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ mr: 1, color: "#667085", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}
                    >
                      Model
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                      {selectedModel ? formatModelName(selectedModel.model) : "Loading models…"}
                    </Typography>
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            display: isMinimized ? "none" : "flex",
            flexDirection: "column",
            position: "relative",
            height: "100%",
          }}
        >
          <ChatInterface
            context={chatContext}
            agentClient={agentClient}
            isOpen={isOpen}
            setNewChatHandler={(handler) => {
              newChatRef.current = handler;
            }}
            setHistoryHandler={(handler) => {
              historyRef.current = handler;
            }}
            onNewMessage={handleAssistantMessage}
            onNotificationPermissionRequest={requestNotificationPermission}
            onLoadingChange={setIsProcessing}
            onProcessOperations={processToolOperations}
            isNavigating={isNavigating}
            modelSelection={selectedModel || undefined}
          />
        </Box>
      </Paper>
    </>
  );
};
